import commonStyleUrl from "../common.css?url"
import { getElement } from "../lib/get-elements"
import { type ObservedAttribute, html } from "../lib/utils"
import { WithAttributeProps, string } from "../mixins/with-attributes-props"

const template = html`
	<style>
		@import url(${commonStyleUrl});
		form div {
			margin: var(--space-m) 0;
		}
	</style>
  <dialog>
    <p><slot>Are you sure you want to proceed?</slot></p>
    <p>Please type <q class="code"></q> below to confirm:</p>
    <form method="dialog">
			<div>
      	<input type="text" name="code">
      	<button class="danger" type="submit" name="action" value="confirm" disabled>
					<slot name="confirm">Confirm</slot>
				</button>
      	<button type="submit" name="action" value="cancel">
					<slot name="cancel">Cancel</slot>
				</button>
			</div>
    </form>
  </dialog>
`

export class HTMLHardConfirmElement extends WithAttributeProps(HTMLElement, {
	code: string(),
}) {
	#dialogElement
	#formElement
	#codeDisplayElement
	#codeInputElement
	#confirmButtonElement

	constructor() {
		super()
		let shadowRoot = this.attachShadow({ mode: "closed" })
		shadowRoot.appendChild(template.content.cloneNode(true))
		this.#dialogElement = getElement("dialog", shadowRoot, HTMLDialogElement)
		this.#dialogElement.returnValue = "cancel"
		this.#formElement = getElement("form", this.#dialogElement, HTMLFormElement)
		this.#codeDisplayElement = getElement(".code", this.#dialogElement)
		this.#codeInputElement = getElement(
			"input[name=code]",
			this.#formElement,
			HTMLInputElement,
		)
		this.#confirmButtonElement = getElement(
			"button[type=submit][name=action][value=confirm]",
			this.#formElement,
			HTMLButtonElement,
		)
	}

	connectedCallback() {
		this.#codeDisplayElement.textContent = this.code
		this.#codeInputElement.addEventListener("input", this.#updateSubmitButton)
		this.#dialogElement.addEventListener("click", this.#handleClick)
		this.#formElement.addEventListener("submit", this.#handleFormSubmit)
	}

	disconnectedCallback() {
		this.#codeInputElement.removeEventListener(
			"input",
			this.#updateSubmitButton,
		)
		this.#dialogElement.removeEventListener("click", this.#handleClick)
		this.#formElement.removeEventListener("submit", this.#handleFormSubmit)
	}

	static observedAttributes = ["code"] as const

	attributeChangedCallback(
		_name: ObservedAttribute<typeof HTMLHardConfirmElement>,
		_oldValue: string,
		_newValue: string,
	) {
		this.#codeDisplayElement.textContent = this.code
	}

	show() {
		this.#formElement.reset()
		this.#updateSubmitButton()
		this.#dialogElement.returnValue = "cancel"
		this.#dialogElement.showModal()
	}

	#updateSubmitButton = () => {
		this.#confirmButtonElement.disabled = !this.#checkCodeInput()
	}

	#checkCodeInput() {
		return this.#codeInputElement.value === this.code
	}

	#handleClick = (event: MouseEvent) => {
		let rect = this.#dialogElement.getBoundingClientRect()
		let isInDialog =
			rect.top <= event.clientY &&
			event.clientY <= rect.top + rect.height &&
			rect.left <= event.clientX &&
			event.clientX <= rect.left + rect.width
		if (!isInDialog) {
			this.#close("cancel")
		}
	}

	#close(action: "confirm" | "cancel") {
		this.#dialogElement.close(action)
		this.dispatchEvent(
			action === "confirm" ? new ConfirmEvent() : new CancelEvent(),
		)
	}

	#handleFormSubmit = (event: SubmitEvent) => {
		event.preventDefault()
		// Today I learned the submit button's value is not included in the form data
		// from the event, even if it is submitted to the server when using HTML
		// default behavior.
		const isConfirmed =
			this.#checkCodeInput() &&
			event.submitter !== null &&
			"value" in event.submitter &&
			event.submitter.value === "confirm"
		this.#close(isConfirmed ? "confirm" : "cancel")
	}
}

customElements.define("x-hard-confirm", HTMLHardConfirmElement)

export class ConfirmEvent extends CustomEvent<never> {
	constructor() {
		super("confirm")
	}
}

export class CancelEvent extends CustomEvent<never> {
	constructor() {
		super("cancel")
	}
}
